import BigNumber from "bignumber.js";
import { BASE18 } from "src/configs/constance";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import NFTCoreContract from "src/contract-logic/logic/NFTCoreContract";
import NFTMarketplaceCollectionContract from "src/contract-logic/logic/NFTMarketplaceCollectionContract";
import NFTSellContract from "src/contract-logic/logic/NFTSellContract";
import { isNullAddress, shuffleArray } from "src/utils";
import { CollectionName, RarityMapping, TypeMapping } from "src/utils/knight";
import multiCall from "src/utils/multicall";
import { SpecialCollectionForSell } from "src/utils/special-knight";

export default class SellingMarketplaceQuery {
  static async fetchLimited() {
    let [expList, orderList, owner] = await Promise.allSettled([
      multiCall(
        NFTCollectionContract.ABI,
        SpecialCollectionForSell.map((tokenId) => ({
          address: NFTCollectionContract.ADDRESS,
          name: "getCollectionExperience",
          params: [tokenId],
        }))
      ),
      multiCall(
        NFTMarketplaceCollectionContract.ABI,
        SpecialCollectionForSell.map((tokenId) => ({
          address: NFTMarketplaceCollectionContract.ADDRESS,
          name: "getTokenOrder",
          params: [tokenId],
        }))
      ),
      multiCall(
        NFTCollectionContract.ABI,
        SpecialCollectionForSell.map((tokenId) => ({
          address: NFTCollectionContract.ADDRESS,
          name: "ownerOf",
          params: [tokenId],
        }))
      ),
    ]);
    expList = expList.value.flat();
    orderList = orderList.value.flat();
    owner = owner.status == "fulfilled" ? owner.value.flat() : [];
    return orderList.map((item, index) => {
      if (isNullAddress(item.nftSeller)) {
        return {
          id: SpecialCollectionForSell[index],
          exp: parseInt(expList[index]),
          seller: owner?.[index] || NFTMarketplaceCollectionContract.ADDRESS,
          isSoldOut: true,
          isSpecial: true,
        };
      }
      return {
        id: SpecialCollectionForSell[index],
        price: BigNumber(item.price._hex).dividedBy(BASE18).toNumber(),
        exp: parseInt(expList[index]),
        seller: item.nftSeller,
        isSpecial: true,
      };
    });
  }

  static async _fetchNormal(tokenIdSlice) {
    const [tokenOrders, tokenMetadata] = await Promise.all([
      multiCall(
        NFTSellContract.ABI,
        tokenIdSlice.map((tokenId) => ({
          address: NFTSellContract.ADDRESS,
          name: "getTokenOrder",
          params: [tokenId],
        }))
      ),
      multiCall(
        NFTCoreContract.ABI,
        tokenIdSlice.map((tokenId) => ({
          address: NFTCoreContract.ADDRESS,
          name: "getTokenMetadata",
          params: [tokenId],
        }))
      ),
    ]);
    const tokenOrdersFlattened = tokenOrders.flat();
    const tokensMetadataFlattened = tokenMetadata.flat();
    let v1 = [];
    let v2 = [];
    let counter = 0;
    for (const tokenData of tokensMetadataFlattened) {
      const collectionId = parseInt(tokenData.collectionId);
      const collectionName = CollectionName[collectionId - 1];
      const rarity = parseInt(tokenData.tokenRarity);
      if (collectionName && rarity >= 1) {
        const type = parseInt(tokenData.tokenType);
        const tType = TypeMapping[type - 1];
        const tRarity = RarityMapping[rarity - 1];
        const id = parseInt(tokenIdSlice[counter]);
        const exp = parseInt(tokenData.experiencePoint);
        const price = BigNumber(tokenOrdersFlattened[counter].price._hex).dividedBy(BASE18).toNumber();
        const seller = tokenOrdersFlattened[counter].nftSeller;
        const data = {
          id,
          collectionName,
          collectionId,
          nRarity: rarity,
          nType: type,
          rarity: tRarity,
          type: tType,
          exp,
          price,
          seller,
        };
        if (collectionId == 1) v1.push(data);
        else if (collectionId == 2) v2.push(data);
      }
      counter++;
    }
    v1 = v1.sort((item1, item2) => item2.nRarity - item1.nRarity || item2.exp - item1.exp || item1.nType - item2.nType);
    v2 = v2.sort((item1, item2) => item2.nRarity - item1.nRarity || item2.exp - item1.exp || item1.nType - item2.nType);
    return { v1, v2 };
  }

  static async fetchNormal(chunk = 500) {
    const total = await NFTSellContract.getTokenOnSaleCount();
    const [tokenIds] = await Promise.all([
      multiCall(
        NFTSellContract.ABI,
        new Array(total).fill(1).map((_, idx) => ({
          address: NFTSellContract.ADDRESS,
          name: "getTokenOnSaleAtIndex",
          params: [idx],
        }))
      ),
    ]);
    const promises = [];
    const tokenIdsFlattened = tokenIds.flat();
    shuffleArray(tokenIdsFlattened);
    for (var i = 0; i < tokenIdsFlattened.length; i += chunk) {
      const _tokenSlice = tokenIdsFlattened.slice(i, i + chunk);
      promises.push(this._fetchNormal(_tokenSlice));
    }
    const result = await Promise.all(promises);
    if (result.length > 0) return result[0];
    else return { v1: [], v2: [] };
  }
}
